import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn, UserPlus, Mail, Lock, ArrowRight, ArrowLeft, MapPin, Calendar, Hash, User } from "lucide-react";
import { useAuth, formatApiError } from "./AuthContext";

const input =
  "w-full rounded-xl bg-white border border-ink-700/10 px-3 py-2.5 text-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-gold-500";

const Frame = ({ children }) => (
  <div className="min-h-screen bg-ivory-50 text-ink-900 flex flex-col">
    <header className="max-w-5xl w-full mx-auto px-6 pt-8 flex items-center justify-between">
      <a href="/" className="flex items-center gap-3">
        <img src="/moonshine-logo.png" alt="Moonshine Solutions" className="h-14 w-14 rounded-full object-cover ring-1 ring-gold-500/30" />
        <div className="leading-tight">
          <div className="font-display font-medium text-ink-900 text-[15px]">Moonshine</div>
          <div className="font-sans text-[11px] uppercase tracking-[0.18em] text-ink-500">Solutions</div>
        </div>
      </a>
      <a href="/" className="inline-flex items-center gap-2 text-sm text-ink-500 hover:text-gold-700 transition">
        <ArrowLeft className="w-4 h-4" /> Zur Startseite
      </a>
    </header>
    <main className="flex-1 flex items-center justify-center px-6 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">
        {children}
      </motion.div>
    </main>
  </div>
);

export const Login = () => {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(email.trim(), pw);
      const from = loc.state?.from;
      // If the user tried to reach a specific page (e.g. /Gold), honour it —
      // even for admins. Only when no origin is known do we send admins to
      // their default landing (/backoffice) and GPs to their partner dashboard.
      if (from) nav(from, { replace: true });
      else if (u.role === "admin") nav("/backoffice", { replace: true });
      else nav("/partner", { replace: true });
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <Frame>
      <div className="text-xs uppercase tracking-[0.2em] text-gold-700 mb-3">Login</div>
      <h1 className="font-display text-4xl font-medium mb-6">Willkommen zurück</h1>
      <form onSubmit={submit} className="rounded-2xl bg-white border border-ink-700/10 p-6 space-y-4" data-testid="login-form">
        <div>
          <label className="text-xs uppercase tracking-widest text-ink-500 flex items-center gap-1.5"><Mail className="w-3 h-3" /> E-Mail</label>
          <input type="email" required className={input} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-ink-500 flex items-center gap-1.5"><Lock className="w-3 h-3" /> Passwort</label>
          <input type="password" required minLength={8} className={input} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="login-password" />
        </div>
        {err && <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700" data-testid="login-error">{err}</div>}
        <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-ink-900 hover:bg-ink-700 disabled:bg-ink-500 text-gold-300 px-6 py-3 text-sm font-medium transition" data-testid="login-submit">
          <LogIn className="w-4 h-4" /> {loading ? "Anmeldung …" : "Einloggen"}
        </button>
        <div className="text-center text-sm text-ink-500 pt-2">
          Noch kein Konto? <Link to="/register" className="text-gold-700 hover:underline" data-testid="link-register">Jetzt registrieren</Link>
        </div>
      </form>
    </Frame>
  );
};

export const Register = () => {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", address: "", birthdate: "", gp_number: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await register({ ...form, email: form.email.trim().toLowerCase() });
      nav("/pending", { replace: true, state: { email: form.email } });
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <Frame>
      <div className="text-xs uppercase tracking-[0.2em] text-gold-700 mb-3">Registrierung Geschäftspartner</div>
      <h1 className="font-display text-4xl font-medium mb-2">Konto erstellen</h1>
      <p className="text-sm text-ink-500 mb-6">Nach der Registrierung prüft Pascal Schwab deine Angaben und schaltet dich frei.</p>
      <form onSubmit={submit} className="rounded-2xl bg-white border border-ink-700/10 p-6 space-y-4" data-testid="register-form">
        <div>
          <label className="text-xs uppercase tracking-widest text-ink-500 flex items-center gap-1.5"><User className="w-3 h-3" /> Vor- und Nachname *</label>
          <input required className={input} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="reg-name" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-ink-500 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> Adresse *</label>
          <input required className={input} placeholder="Straße Nr., PLZ Ort, Land" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="reg-address" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-ink-500 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Geburtsdatum *</label>
            <input required placeholder="TT.MM.JJJJ" className={input} value={form.birthdate} onChange={(e) => set("birthdate", e.target.value)} data-testid="reg-birthdate" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-ink-500 flex items-center gap-1.5"><Hash className="w-3 h-3" /> GP-Nummer *</label>
            <input required className={input} value={form.gp_number} onChange={(e) => set("gp_number", e.target.value)} data-testid="reg-gp" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-ink-500 flex items-center gap-1.5"><Mail className="w-3 h-3" /> E-Mail *</label>
          <input required type="email" className={input} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="reg-email" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-ink-500 flex items-center gap-1.5"><Lock className="w-3 h-3" /> Passwort *</label>
          <input required type="password" minLength={8} placeholder="mind. 8 Zeichen" className={input} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="reg-password" />
        </div>
        {err && <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700" data-testid="register-error">{err}</div>}
        <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-ink-900 hover:bg-ink-700 disabled:bg-ink-500 text-gold-300 px-6 py-3 text-sm font-medium transition" data-testid="register-submit">
          <UserPlus className="w-4 h-4" /> {loading ? "Sende …" : "Registrieren"} <ArrowRight className="w-4 h-4" />
        </button>
        <div className="text-center text-sm text-ink-500 pt-2">
          Schon registriert? <Link to="/login" className="text-gold-700 hover:underline" data-testid="link-login">Jetzt einloggen</Link>
        </div>
      </form>
    </Frame>
  );
};

export const PendingApproval = () => {
  const { user, logout } = useAuth();
  return (
    <Frame>
      <div className="rounded-2xl bg-white border border-ink-700/10 p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-gold-100 text-gold-700 flex items-center justify-center mx-auto mb-4 font-display text-2xl">⏳</div>
        <div className="text-xs uppercase tracking-[0.2em] text-gold-700 mb-2">Warten auf Freigabe</div>
        <h1 className="font-display text-2xl md:text-3xl font-medium mb-3">Danke für deine Registrierung!</h1>
        <p className="text-sm text-ink-700 mb-6">
          Deine Anfrage ist eingegangen und wartet auf die manuelle Freigabe. Du bekommst eine E-Mail, sobald dein Konto freigeschaltet ist.
        </p>
        {user ? (
          <button onClick={logout} className="text-sm text-ink-500 hover:text-gold-700 underline" data-testid="pending-logout">Abmelden</button>
        ) : (
          <Link to="/login" className="text-sm text-ink-500 hover:text-gold-700 underline" data-testid="pending-logout">Zur Login-Seite</Link>
        )}
      </div>
    </Frame>
  );
};
